'use client';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { createClient } from '@/lib/supabase/client';
import { THEMES, type ThemeName, type ThemeConfig } from '@/lib/themes-data';

export type { ThemeName, ThemeConfig };
export { THEMES };

interface ThemeContextValue {
  theme: ThemeName;
  themeConfig: ThemeConfig;
  setTheme: (name: ThemeName) => void;
}

const ThemeContext = createContext<ThemeContextValue>({
  theme: 'default',
  themeConfig: THEMES[0],
  setTheme: () => {},
});

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<ThemeName>('anomica');

  const applyThemeVars = (vars: Record<string, string>) => {
    const root = document.documentElement;
    Object.entries(vars).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });
  };

  useEffect(() => {
    // Apply Anomica immediately as the base default before DB fetch
    const anomicaTheme = THEMES.find((t) => t.name === 'anomica')!;
    applyThemeVars(anomicaTheme.vars);

    const supabase = createClient();
    supabase
      .from('site_settings')
      .select('value')
      .eq('key', 'active_theme')
      .single()
      .then(({ data }) => {
        const saved = (data?.value as ThemeName) || 'anomica';
        const found = THEMES.find((t) => t.name === saved);
        if (found) {
          setThemeState(found.name);
          applyThemeVars(found.vars);
        }
      });
  }, []);

  useEffect(() => {
    const config = THEMES.find((t) => t.name === theme) || THEMES[0];
    const root = document.documentElement;
    Object.entries(config.vars).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });
    // Also set legacy CSS variable aliases used by the public business page
    root.style.setProperty('--background', config.vars['--color-bg'] || '');
    root.style.setProperty('--foreground', config.vars['--color-text-primary'] || '');
    root.style.setProperty('--card', config.vars['--color-card-bg'] || '');
    root.style.setProperty('--border', config.vars['--color-card-border'] || '');
    root.style.setProperty('--primary', config.vars['--color-primary'] || '');
    root.style.setProperty('--muted', config.vars['--color-input-bg'] || '');
    root.style.setProperty('--muted-foreground', config.vars['--color-text-secondary'] || '');
    localStorage.setItem('cards247-theme', theme);
    // Update body background
    document.body.style.backgroundColor = config.vars['--color-bg'] || '';
    document.body.style.color = config.vars['--color-text-primary'] || '';
  }, [theme]);

  const setTheme = (name: ThemeName) => {
    setThemeState(name);
    // Persist to Supabase site_settings so all visitors see the same theme
    const supabase = createClient();
    supabase
      .from('site_settings')
      .upsert({ key: 'active_theme', value: name, updated_at: new Date().toISOString() })
      .then(() => {})
      .catch(() => {});
  };

  const themeConfig = THEMES.find((t) => t.name === theme) || THEMES[0];

  return (
    <ThemeContext.Provider value={{ theme, themeConfig, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
